import { useCallback, useEffect, useState } from 'react';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import type { CategoryNode } from '../lib/types';

/**
 * The company's service catalogue: what work is about, as opposed to which project it sits
 * in. Two levels only — "Payments › Payouts" reads, and a third level is where a catalogue
 * stops being maintained.
 */
export function CategoriesCard({ companyId, canManage }: { companyId: string; canManage: boolean }) {
  const [tree, setTree] = useState<CategoryNode[] | null>(null);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [addingTo, setAddingTo] = useState<string | 'root' | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const ask = useAsk();

  const load = useCallback(async () => {
    try {
      setTree(await api.get<CategoryNode[]>(`/companies/${companyId}/categories${includeRetired ? '?includeRetired=true' : ''}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the categories');
    }
  }, [companyId, includeRetired]);

  useEffect(() => {
    void load();
  }, [load]);

  async function add(parentId?: string) {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/companies/${companyId}/categories`, { name: name.trim(), parentId });
      setName('');
      setAddingTo(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that category');
    } finally {
      setBusy(false);
    }
  }

  async function remove(node: CategoryNode) {
    const question =
      node.inUse > 0
        ? `${node.inUse} task${node.inUse === 1 ? '' : 's'} use "${node.name}". It will be retired rather than deleted. Continue?`
        : `Delete "${node.name}"?`;
    if (!(await ask.confirm({ title: question, confirmLabel: 'Yes, remove it', danger: true }))) return;

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.del<{ message: string }>(`/categories/${node.id}`);
      setNotice(result.message);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that category');
    } finally {
      setBusy(false);
    }
  }

  async function setActive(node: CategoryNode, active: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/categories/${node.id}`, { active });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Service catalogue</h2>
        <div className="row">
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            onClick={() => setIncludeRetired(!includeRetired)}
          >
            {includeRetired ? 'Hide retired' : 'Show retired'}
          </button>
          {canManage && (
            <button type="button" className="btn btn-sm" onClick={() => setAddingTo('root')}>
              Add category
            </button>
          )}
        </div>
      </div>

      <div className="card-pad">
        {error && <div className="callout">{error}</div>}
        {notice && <div className="callout callout-ok">{notice}</div>}

        {tree === null ? (
          <span className="dim">Loading…</span>
        ) : tree.length === 0 ? (
          <p className="dim" style={{ margin: 0, fontSize: 13 }}>
            No categories yet. They describe what work is <em>about</em> — Payments, Reporting,
            Access — so tickets can be grouped by service rather than only by project.
          </p>
        ) : (
          <div className="stack-sm">
            {tree.map((parent) => (
              <div key={parent.id}>
                <Row
                  node={parent}
                  canManage={canManage}
                  busy={busy}
                  onAddChild={() => setAddingTo(parent.id)}
                  onRemove={() => remove(parent)}
                  onToggle={(active) => setActive(parent, active)}
                />
                <div style={{ paddingLeft: 22 }}>
                  {(parent.children ?? []).map((child) => (
                    <Row
                      key={child.id}
                      node={child}
                      canManage={canManage}
                      busy={busy}
                      onRemove={() => remove(child)}
                      onToggle={(active) => setActive(child, active)}
                    />
                  ))}
                  {addingTo === parent.id && (
                    <AddRow
                      label={`Subcategory of ${parent.name}`}
                      name={name}
                      setName={setName}
                      busy={busy}
                      onSave={() => add(parent.id)}
                      onCancel={() => {
                        setAddingTo(null);
                        setName('');
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {addingTo === 'root' && (
          <AddRow
            label="New category"
            name={name}
            setName={setName}
            busy={busy}
            onSave={() => add()}
            onCancel={() => {
              setAddingTo(null);
              setName('');
            }}
          />
        )}
      </div>
    </div>
  );
}

function Row({
  node,
  canManage,
  busy,
  onAddChild,
  onRemove,
  onToggle,
}: {
  node: CategoryNode;
  canManage: boolean;
  busy: boolean;
  onAddChild?: () => void;
  onRemove: () => void;
  onToggle: (active: boolean) => void;
}) {
  return (
    <div className="row-between" style={{ padding: '5px 0', opacity: node.active ? 1 : 0.55 }}>
      <span className="row" style={{ gap: 8 }}>
        <span style={{ fontWeight: onAddChild ? 600 : 400 }}>{node.name}</span>
        {node.shared && <span className="badge" title="Offered to every company">group-wide</span>}
        {!node.active && <span className="badge">retired</span>}
        {node.inUse > 0 && (
          <span className="dim" style={{ fontSize: 11 }}>
            {node.inUse} task{node.inUse === 1 ? '' : 's'}
          </span>
        )}
      </span>
      {canManage && !node.shared && (
        <span className="row" style={{ gap: 4 }}>
          {onAddChild && (
            <button type="button" className="btn btn-quiet btn-sm" disabled={busy} onClick={onAddChild}>
              + Sub
            </button>
          )}
          <button type="button" className="btn btn-quiet btn-sm" disabled={busy} onClick={() => onToggle(!node.active)}>
            {node.active ? 'Retire' : 'Restore'}
          </button>
          <button type="button" className="btn btn-quiet btn-sm" disabled={busy} onClick={onRemove}>
            Delete
          </button>
        </span>
      )}
    </div>
  );
}

function AddRow({
  label,
  name,
  setName,
  busy,
  onSave,
  onCancel,
}: {
  label: string;
  name: string;
  setName: (value: string) => void;
  busy: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="row" style={{ gap: 7, marginTop: 8 }}>
      <input
        type="text"
        value={name}
        autoFocus
        placeholder={label}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave();
          if (e.key === 'Escape') onCancel();
        }}
      />
      <button type="button" className="btn btn-primary btn-sm" disabled={busy || !name.trim()} onClick={onSave}>
        Add
      </button>
      <button type="button" className="btn btn-quiet btn-sm" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}
